import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { ErrorCode } from '@sincronia/shared';
import { pt, type MessageKey, type Messages } from './pt.js';
import { ptBR } from './pt-BR.js';
import { ptPT } from './pt-PT.js';

export const LOCALES = ['pt', 'pt-BR', 'pt-PT'] as const;
export type Locale = (typeof LOCALES)[number];

const OVERRIDES: Record<Locale, Partial<Record<MessageKey, string>>> = {
  pt: {},
  'pt-BR': ptBR,
  'pt-PT': ptPT,
};

const STORAGE_KEY = 'sincronia.locale';

function buildMessages(locale: Locale): Messages {
  return { ...pt, ...OVERRIDES[locale] };
}

export function detectLocale(): Locale {
  if (typeof window === 'undefined') return 'pt';

  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && (LOCALES as readonly string[]).includes(stored)) return stored as Locale;

  for (const candidate of navigator.languages ?? [navigator.language]) {
    const normalized = candidate.toLowerCase();
    if (normalized.startsWith('pt-br')) return 'pt-BR';
    if (normalized.startsWith('pt-pt')) return 'pt-PT';
  }
  return 'pt';
}

export type TranslateParams = Record<string, string | number>;
export type Translate = (key: MessageKey, params?: TranslateParams) => string;

type I18nValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Translate;
};

const I18nContext = createContext<I18nValue | null>(null);

function interpolate(template: string, params?: TranslateParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) => {
    const value = params[key];
    return value === undefined ? match : String(value);
  });
}

export function I18nProvider({ children }: { children: ReactNode }): ReactNode {
  const [locale, setLocaleState] = useState<Locale>(() => detectLocale());

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Armazenamento indisponível: o idioma vale apenas para esta sessão.
    }
    document.documentElement.lang = next;
  }, []);

  const value = useMemo<I18nValue>(() => {
    const messages = buildMessages(locale);
    return {
      locale,
      setLocale,
      t: (key, params) => interpolate(messages[key] ?? key, params),
    };
  }, [locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n precisa estar dentro de <I18nProvider>');
  return value;
}

export function useT(): Translate {
  return useI18n().t;
}

/** Chave de tradução de um código de erro do servidor. */
export function errorKey(code: ErrorCode): MessageKey {
  return `errors.${code}`;
}

export type { MessageKey };
